import { TrainingDocument, TrainingVideo } from '../types';

export interface DocumentTraining {
  key: string;
  folderId: number;
  title: string;
  trainingDate: string | null;
  documents: TrainingDocument[];
  isCompleted: boolean;
}

export interface TrainingMaterials {
  videos: TrainingVideo[];
  documents: TrainingDocument[];
}

export interface TrainingSession extends DocumentTraining {
  videos: TrainingVideo[];
}

/** A date only associates materials inside the same folder. Undated files stay independent. */
export function relatedTrainingMaterials(
  primary: TrainingVideo | TrainingDocument,
  primaryKind: 'video' | 'document',
  videos: TrainingVideo[],
  documents: TrainingDocument[],
): TrainingMaterials {
  const sameTraining = (item: TrainingVideo | TrainingDocument) => Boolean(primary.training_date)
    && item.folder_id === primary.folder_id && item.training_date === primary.training_date;
  return {
    videos: primaryKind === 'video'
      ? [primary as TrainingVideo, ...videos.filter(item => item.id !== primary.id && sameTraining(item))]
      : videos.filter(sameTraining),
    documents: primaryKind === 'document'
      ? [primary as TrainingDocument, ...documents.filter(item => item.id !== primary.id && sameTraining(item))]
      : documents.filter(sameTraining),
  };
}

/** Never choose one of several possible files without an explicit link or user selection. */
export function selectedTrainingMaterial<T extends { id: number }>(items: T[], queryId: string | null, primaryId?: number): T | undefined {
  if (primaryId !== undefined) return items.find(item => item.id === primaryId);
  if (queryId !== null) return /^[1-9]\d*$/.test(queryId) ? items.find(item => item.id === Number(queryId)) : undefined;
  return items.length === 1 ? items[0] : undefined;
}

export function groupTrainingMaterials(videos: TrainingVideo[], documents: TrainingDocument[]): TrainingSession[] {
  const groups = new Map<string, TrainingSession>();
  const add = (item: TrainingVideo | TrainingDocument, kind: 'video' | 'document') => {
    const key = `${item.folder_id}:${item.training_date || `undated-${kind}-${item.id}`}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key, folderId: item.folder_id, trainingDate: item.training_date || null,
        title: item.training_date ? `Обучение ${new Date(`${item.training_date}T00:00:00`).toLocaleDateString('ru-RU')}` : item.title,
        videos: [], documents: [], isCompleted: false,
      };
      groups.set(key, group);
    }
    if (kind === 'video') group.videos.push(item as TrainingVideo);
    else group.documents.push(item as TrainingDocument);
  };
  videos.forEach(item => add(item, 'video'));
  documents.forEach(item => add(item, 'document'));
  return Array.from(groups.values()).map(group => ({
    ...group,
    isCompleted: [...group.videos, ...group.documents].every(item => item.progress?.is_completed),
  })).sort((left, right) => (right.trainingDate || '').localeCompare(left.trainingDate || ''));
}

export function groupTrainingDocuments(documents: TrainingDocument[]): DocumentTraining[] {
  const groups = new Map<string, DocumentTraining>();
  documents.forEach(document => {
    const key = `${document.folder_id}:${document.training_date || `undated-${document.id}`}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key, folderId: document.folder_id,
        trainingDate: document.training_date || null,
        title: document.training_date
          ? `Обучение ${new Date(`${document.training_date}T00:00:00`).toLocaleDateString('ru-RU')}`
          : document.title,
        documents: [], isCompleted: false,
      };
      groups.set(key, group);
    }
    group.documents.push(document);
  });
  return Array.from(groups.values()).map(group => ({
    ...group,
    documents: group.documents.sort((left, right) => left.document_kind.localeCompare(right.document_kind) || left.sort_order - right.sort_order || left.id - right.id),
    isCompleted: group.documents.every(document => document.progress?.is_completed),
  })).sort((left, right) => (right.trainingDate || '').localeCompare(left.trainingDate || ''));
}
